import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib';
import { ADMIN_USER_QUERY_KEYS } from '@/constants';
import { useAuth } from './useAuth';

/**
 * Keeps the signed-in admin's permissions current when a super_admin grants or
 * revokes something for them: subscribes to postgres_changes on
 * admin_permission_grants and invalidates the permissions query, so a revoked
 * Reports tab disappears within seconds rather than after the 5-minute cache.
 * Mount ONCE (Layout), not per usePermissions caller.
 *
 * Deliberately unfiltered. INSERT/UPDATE events are scoped by RLS to rows the
 * admin may read (their own; every row for a super_admin). DELETE events are
 * not RLS-filtered and, with the table's default replica identity, carry only
 * the row id — so a column filter such as `admin_user_id=eq.<me>` can never
 * match a revoke. Reacting to every event costs one cheap refetch of the
 * admin's own permissions and guarantees revokes are seen.
 *
 * Why not a per-admin Broadcast topic: database broadcasts (`realtime.send`)
 * silently no-op on this project (no realtime.messages partitions), so
 * postgres_changes is the only push channel available.
 *
 * If the channel drops, nothing breaks: the query still expires normally.
 */
export function useAdminGrantsRealtime(): void {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;

  useEffect(() => {
    if (!userId) return;
    // Older test doubles of the client have no realtime surface.
    if (typeof supabase.channel !== 'function') return;

    const channel = supabase
      .channel(`admin-grants:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'admin_permission_grants' },
        () => {
          queryClient.invalidateQueries({ queryKey: ADMIN_USER_QUERY_KEYS.CURRENT_PERMISSIONS });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}
